/**
 * Tracks user sessions for VV current_users / analytics.
 * - Starts a session when the component mounts
 * - Sends heartbeats periodically to keep the session alive
 * - Ends the session when the page unloads using sendBeacon
 */

import { useCallback, useEffect, useRef } from 'react';
import API, { sendSessionEndBeacon } from '../API';

const DEFAULT_HEARTBEAT_MS = 30000;

interface UseWebSessionOptions {
  eventId: string;
  accessToken: string | null;
  userId?: string;
  enabled?: boolean;
  heartbeatIntervalMs?: number;
}

export function useWebSession({
  eventId,
  accessToken,
  userId,
  enabled = true,
  heartbeatIntervalMs = DEFAULT_HEARTBEAT_MS,
}: UseWebSessionOptions) {
  const sessionIdRef = useRef<string | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const userIdRef = useRef<string | null>(userId ?? null);

  useEffect(() => {
    if (userId !== undefined) {
      userIdRef.current = userId;
    }
  }, [userId]);

  const startSession = useCallback(async () => {
    if (!accessToken || !eventId || !enabled) {
      if (enabled && (!accessToken || !eventId)) {
        console.warn('[WebSession] Skipping session start: missing', !accessToken ? 'accessToken' : 'eventId');
      }
      return;
    }

    try {
      const response = await API.startWebSession(accessToken, { eventId });

      if (response.ok) {
        const data = await response.json();
        sessionIdRef.current = data.session_id;
        if (data.user_id) {
          userIdRef.current = data.user_id;
        }
      } else {
        const text = await response.text();
        console.warn('[WebSession] Session start failed:', response.status, text || response.statusText);
      }
    } catch (err) {
      console.error('[WebSession] Error starting session:', err);
    }
  }, [eventId, enabled, accessToken]);

  const sendHeartbeat = useCallback(async () => {
    if (!accessToken || !eventId || !enabled) return;

    try {
      const response = await API.heartbeatWebSession(accessToken, { eventId });
      if (!response.ok) {
        console.warn('[WebSession] Heartbeat failed:', response.status);
      }
    } catch (err) {
      console.error('[WebSession] Error sending heartbeat:', err);
    }
  }, [eventId, enabled, accessToken]);

  const endSession = useCallback(() => {
    if (!eventId) return;
    sendSessionEndBeacon(eventId, userIdRef.current ?? '', sessionIdRef.current ?? undefined);
    sessionIdRef.current = null;
  }, [eventId]);

  const endSessionWithAuth = useCallback(async (reason?: 'idle' | 'leave') => {
    if (!accessToken || !eventId) return;
    if (!enabled && !reason) return;

    try {
      await API.endWebSession(accessToken, { eventId, reason });
      sessionIdRef.current = null;
    } catch (err) {
      console.error('[WebSession] Error ending session:', err);
    }
  }, [eventId, enabled, accessToken]);

  useEffect(() => {
    if (!enabled || !accessToken || !eventId) return;

    startSession();

    heartbeatIntervalRef.current = setInterval(sendHeartbeat, heartbeatIntervalMs);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        sendHeartbeat();
      }
    };

    const handleBeforeUnload = () => {
      endSession();
    };

    const handlePageHide = () => {
      endSession();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      endSessionWithAuth();
    };
  }, [
    enabled,
    accessToken,
    eventId,
    startSession,
    sendHeartbeat,
    endSession,
    endSessionWithAuth,
    heartbeatIntervalMs,
  ]);

  return {
    endSession: endSessionWithAuth,
  };
}
